import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthFetch } from '../lib/useAuthFetch';
import { Card, StatTile, Pill, SectionLabel, DataTable } from '../components/ui';
import type { Column } from '../components/ui';
import {
  formatMenuUpdatedAt,
  menuRefreshHealth,
  menuRefreshLabel,
  type MenuRefreshStatus,
} from '../lib/menuRefresh';

const API = import.meta.env.VITE_API_URL ?? '';

interface Location {
  id: string;
  name: string;
  address: string;
  market: string;
  mktClass: string;
  license: string;
  health: 'ok' | 'warn' | 'fail';
  healthLabel: string;
  lastRun: string;
  items: number;
  track: number;
  block: number;
  active: boolean;
  log: { s: string; t: string; n: string }[];
  competitors: string[];
  blocked: string[];
  unavailableCompetitorId?: string;
}

interface RefreshTarget {
  kind: 'own_location' | 'competitor';
  id: string;
  name: string;
  status: MenuRefreshStatus;
  last_updated_at: string | null;
  item_count: number;
}

interface RefreshStatus {
  status: MenuRefreshStatus;
  last_updated_at: string | null;
  item_count: number;
  targets: RefreshTarget[];
}

function healthColor(h: string) {
  if (h === 'ok') return 'var(--accent)';
  if (h === 'warn') return 'var(--warm)';
  return 'var(--danger)';
}

function logDotColor(s: string) {
  if (s === 'ok') return 'var(--accent)';
  if (s === 'warn') return 'var(--warm)';
  return 'var(--danger)';
}

const TIER_STYLE: Record<string, React.CSSProperties> = {
  elite: { background: 'var(--rose-soft)', color: 'var(--rose)' },
  hot: { background: 'var(--warm-soft)', color: 'var(--warm)' },
  competitive: { background: 'rgba(84,132,164,0.14)', color: 'var(--slate)' },
  standard: { background: 'var(--surface-3)', color: 'var(--text-2)' },
};

/** Market-tier badge on the shared Pill (chip variant), tinted per tier. */
function TierPill({ market, cls }: { market: string; cls: string }) {
  return (
    <Pill variant="chip" style={{ ...(TIER_STYLE[cls] ?? TIER_STYLE.standard), fontWeight: 700, letterSpacing: '0.06em', padding: '3px 8px' }}>
      {market}
    </Pill>
  );
}

function makeDisplayLocation(
  l: { id: string; name: string; address: string; dcc_license?: string | null; active: boolean; lat?: number | null; lng?: number | null },
  refresh?: RefreshStatus,
): Location {
  const rivals = refresh?.targets.filter((target) => target.kind === 'competitor') ?? [];
  return {
    id: l.id,
    name: l.name,
    address: l.address ?? '—',
    market: 'Standard',
    mktClass: 'standard',
    license: l.dcc_license ?? '—',
    health: refresh ? menuRefreshHealth(refresh.status) : 'warn',
    healthLabel: refresh ? menuRefreshLabel(refresh.status) : 'Status unavailable',
    lastRun: formatMenuUpdatedAt(refresh?.last_updated_at ?? null),
    items: refresh?.item_count ?? 0,
    track: rivals.length,
    block: 0,
    active: l.active,
    log: refresh?.targets.slice(0, 6).map((target) => ({
      s: menuRefreshHealth(target.status),
      t: formatMenuUpdatedAt(target.last_updated_at).replace('Updated ', ''),
      n: `${target.name}: ${menuRefreshLabel(target.status)}`,
    })) ?? [],
    competitors: rivals.map((target) => target.name),
    blocked: [],
    unavailableCompetitorId: rivals.find((target) => target.status === 'unavailable')?.id,
  };
}

const colGrid = '2fr 1.3fr 1fr 1fr 1fr 1fr 190px';

/* ── layout-only styles; every visual block comes from components/ui ── */
const s = {
  root: { display: 'flex', flexDirection: 'column' as const, height: '100%', background: 'var(--bg)', fontFamily: 'var(--sans)', fontSize: 14, color: 'var(--text-1)' },
  topbar: { background: 'var(--surface)', backdropFilter: 'blur(12px)', borderBottom: '1px solid var(--border)', padding: '0 28px', flexShrink: 0 },
  tbInner: { display: 'flex', alignItems: 'center', gap: 14, padding: '12px 0 11px' },
  back: { display: 'flex', alignItems: 'center', gap: 5, fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0, padding: 0 },
  tbDivider: { width: 1, height: 16, background: 'var(--border-2)', flexShrink: 0 },
  tbTitle: { fontFamily: 'var(--display)', fontSize: 22, fontWeight: 400, color: 'var(--text-1)', letterSpacing: '-0.015em', lineHeight: 1 },
  tbSub: { fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', letterSpacing: '0.06em', textTransform: 'uppercase' as const, marginTop: 3 },
  tbActions: { marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 },
  statsBar: { padding: '12px 28px', borderBottom: '1px solid var(--border)', background: 'var(--surface-2)', display: 'flex', gap: 10, alignItems: 'center', flexShrink: 0, flexWrap: 'wrap' as const },
  statsSep: { width: 1, height: 30, background: 'var(--border-2)', flexShrink: 0, margin: '0 2px' },
  spyStatus: { marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)' },
  content: { flex: 1, overflowY: 'auto' as const, padding: '24px 28px', minHeight: 0 },
  section: { marginBottom: 20, padding: 0, overflow: 'hidden' as const },
  secHead: { padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'baseline', gap: 6, background: 'var(--surface-2)' },
  secTitle: { fontSize: 13, fontWeight: 700, color: 'var(--text-1)' },
  secSub: { fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)' },
  headerRow: { display: 'grid', gridTemplateColumns: colGrid, background: 'var(--surface-2)', borderBottom: '1px solid var(--border)' },
  th: (align: 'left' | 'center' | 'right') => ({ padding: '10px 16px', fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.10em', textTransform: 'uppercase' as const, color: 'var(--text-3)', fontWeight: 700, whiteSpace: 'nowrap' as const, textAlign: align }),
  rowWrap: (active: boolean) => ({ borderBottom: '1px solid var(--border)', opacity: active ? 1 : 0.55 }),
  row: { display: 'grid', gridTemplateColumns: colGrid, alignItems: 'center', cursor: 'pointer' },
  cell: { padding: '13px 16px' } as React.CSSProperties,
  cellC: { padding: '13px 16px', textAlign: 'center' as const },
  nameCell: { padding: '13px 16px', display: 'flex', alignItems: 'center', gap: 10 },
  chev: (open: boolean): React.CSSProperties => ({ width: 18, height: 18, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: '1px solid var(--border-2)', color: 'var(--text-3)' , transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }),
  locName: { fontSize: 13, fontWeight: 700, color: 'var(--text-1)' },
  locAddr: { fontSize: 10, color: 'var(--text-3)', marginTop: 1, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const },
  healthWrap: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 },
  dot: (c: string) => ({ width: 7, height: 7, borderRadius: '50%', background: c, flexShrink: 0 }),
  monoSm: (c: string) => ({ fontFamily: 'var(--mono)', fontSize: 10, color: c }),
  monoXs: (c: string) => ({ fontFamily: 'var(--mono)', fontSize: 9, color: c }),
  slots: { textAlign: 'center' as const, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 700 },
  statusPill: (active: boolean): React.CSSProperties => ({ padding: '3px 9px', borderRadius: 10, ...(active ? { background: 'var(--accent-soft)', color: 'var(--accent)', border: '1px solid rgba(11,184,184,0.22)' } : { background: 'var(--surface-3)', color: 'var(--text-3)', border: '1px solid var(--border)' }) }),
  actions: { padding: '13px 16px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 5 },
  rowBtn: { display: 'flex', alignItems: 'center', gap: 4, padding: '5px 9px', borderRadius: 6, fontSize: 10, fontWeight: 600, cursor: 'pointer', border: '1px solid var(--border-2)', background: 'transparent', color: 'var(--text-2)', fontFamily: 'var(--sans)' } as React.CSSProperties,
  rowBtnDanger: { display: 'flex', alignItems: 'center', gap: 4, padding: '5px 9px', borderRadius: 6, fontSize: 10, fontWeight: 600, cursor: 'pointer', border: '1px solid rgba(211,150,166,0.35)', background: 'transparent', color: 'var(--rose)', fontFamily: 'var(--sans)' } as React.CSSProperties,
  detail: { borderTop: '1px solid var(--border)', background: 'var(--surface-2)' },
  detailGrid: { padding: '16px 20px 16px 54px', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20 },
  dsRow: (last: boolean): React.CSSProperties => ({ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '5px 0', borderBottom: last ? 'none' : '1px solid var(--border)' }),
  dsKey: { fontSize: 11, color: 'var(--text-2)' },
  dsVal: (c: string) => ({ fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 600, color: c }),
  emptyMini: { fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', lineHeight: 1.6, padding: '6px 0' },
  spyLogRow: { display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px', borderRadius: 5, background: 'var(--surface-3)' },
  detailActions: { padding: '10px 20px 14px 54px', display: 'flex', gap: 8, borderTop: '1px solid var(--border)', flexWrap: 'wrap' as const },
  centerBlock: { display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 },
  modalOverlay: { position: 'fixed' as const, inset: 0, background: 'rgba(10,20,28,0.55)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modal: { background: 'var(--surface)', border: '1px solid var(--border-2)', borderRadius: 'var(--r)', boxShadow: '0 20px 60px rgba(0,0,0,0.25)', padding: 24, width: 420, maxWidth: '90vw' },
  modalTitle: { fontFamily: 'var(--display)', fontSize: 19, fontWeight: 400, color: 'var(--text-1)', marginBottom: 6, letterSpacing: '-0.01em' },
  modalSub: { fontSize: 12, color: 'var(--text-2)', marginBottom: 20, lineHeight: 1.6 },
  formRow: { marginBottom: 14 },
  formLabel: { fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase' as const, color: 'var(--text-2)', marginBottom: 5, display: 'block' },
  formInput: { width: '100%', padding: '8px 11px', borderRadius: 'var(--r-sm)', border: '1.5px solid var(--border-2)', background: 'var(--surface-2)', color: 'var(--text-1)', fontFamily: 'var(--sans)', fontSize: 13, outline: 'none' } as React.CSSProperties,
  modalFooter: { display: 'flex', gap: 8, justifyContent: 'flex-end' },
  toast: { position: 'fixed' as const, bottom: 24, left: '50%', transform: 'translateX(-50%)', background: 'var(--text-1)', color: 'var(--surface)', padding: '9px 18px', borderRadius: 20, fontSize: 12, fontWeight: 600, fontFamily: 'var(--mono)', zIndex: 9000, letterSpacing: '0.04em' },
};

/* ── small svg helpers ── */
const Ic = {
  chev: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ width: 10, height: 10 }}><polyline points="9 18 15 12 9 6" /></svg>,
  back: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ width: 12, height: 12 }}><polyline points="15 18 9 12 15 6" /></svg>,
  refresh: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 12, height: 12 }}><polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 .49-3.51" /></svg>,
  plus: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ width: 12, height: 12 }}><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>,
  dash: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 11, height: 11 }}><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>,
  search: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 11, height: 11 }}><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>,
  spy: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 11, height: 11 }}><polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 .49-3.51" /></svg>,
  edit: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 11, height: 11 }}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>,
};

interface CompRow { name: string; blocked: boolean }

export default function LocationManagement() {
  const navigate = useNavigate();
  const authFetch = useAuthFetch();
  const [locs, setLocs] = useState<Location[]>([]);
  const [locsLoading, setLocsLoading] = useState(true);
  const [locsError, setLocsError] = useState(false);
  const [refreshing, setRefreshing] = useState<Set<string>>(new Set());

  const loadLocs = useCallback(async () => {
    setLocsLoading(true);
    setLocsError(false);
    try {
      const response = await authFetch(`${API}/api/v1/locations`);
      if (!response.ok) throw new Error('locations failed');
      const res = await response.json();
      const locations = res.locations ?? [];
      const withStatus = await Promise.all(locations.map(async (location: any) => {
        try {
          const statusResponse = await authFetch(`${API}/api/v1/menu-refresh/locations/${location.id}`);
          if (!statusResponse.ok) return makeDisplayLocation(location);
          const statusBody = await statusResponse.json();
          return makeDisplayLocation(location, statusBody.data);
        } catch {
          return makeDisplayLocation(location);
        }
      }));
      setLocs(withStatus);
    } catch {
      setLocsError(true);
    } finally {
      setLocsLoading(false);
    }
  }, [authFetch]);

  useEffect(() => { loadLocs(); }, [loadLocs]);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const active = locs.filter(l => l.active);
  const inactive = locs.filter(l => !l.active);
  const totalTrack = locs.reduce((a, l) => a + l.track, 0);
  const totalBlock = locs.reduce((a, l) => a + l.block, 0);
  const totalSkus = locs.reduce((a, l) => a + l.items, 0);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2400);
  };

  const requestRefresh = async (locationId: string) => {
    setRefreshing((current) => new Set(current).add(locationId));
    try {
      const response = await authFetch(`${API}/api/v1/menu-refresh/locations/${locationId}`, { method: 'POST' });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || 'Update could not be queued');
      const unavailable = Number(body.unavailable) || 0;
      showToast(unavailable
        ? 'Update queued · Some menus need review'
        : 'Menu update queued');
      await loadLocs();
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Update could not be queued');
    } finally {
      setRefreshing((current) => {
        const next = new Set(current);
        next.delete(locationId);
        return next;
      });
    }
  };

  const requestAllRefreshes = async () => {
    const ids = active.map((location) => location.id);
    if (!ids.length) return;
    setRefreshing(new Set(ids));
    try {
      const responses = await Promise.all(ids.map((id) =>
        authFetch(`${API}/api/v1/menu-refresh/locations/${id}`, { method: 'POST' })
      ));
      if (responses.some((response) => !response.ok)) throw new Error('Some updates could not be queued');
      showToast(`Menu updates queued for ${ids.length} location${ids.length === 1 ? '' : 's'}`);
      await loadLocs();
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Updates could not be queued');
    } finally {
      setRefreshing(new Set());
    }
  };

  const deactivate = (id: string) => {
    setLocs(prev => prev.map(l => l.id === id ? { ...l, active: false } : l));
    setExpanded(null);
    setShowDeactivateModal(null);
    const loc = locs.find(l => l.id === id);
    if (loc) showToast(loc.name + ' deactivated');
  };

  const reactivate = (id: string) => {
    setLocs(prev => prev.map(l => l.id === id ? { ...l, active: true } : l));
    const loc = locs.find(l => l.id === id);
    if (loc) showToast(loc.name + ' reactivated');
  };

  const compColumns: Column<CompRow>[] = [
    { key: 'name', header: 'Competitor', mono: false },
    {
      key: 'status', header: 'Status', align: 'right',
      render: (r) => <Pill tone="tracking" variant="chip">Tracking</Pill>,
    },
  ];

  function LocRow({ loc }: { loc: Location }) {
    const isExpanded = expanded === loc.id;
    return (
      <div style={s.rowWrap(loc.active)} data-testid="location-row">
        {/* Main row */}
        <div onClick={() => setExpanded(isExpanded ? null : loc.id)} style={s.row}>
          {/* Name */}
          <div style={s.nameCell}>
            <div style={s.chev(isExpanded)}>{Ic.chev}</div>
            <div style={{ minWidth: 0 }}>
              <div style={s.locName}>{loc.name}</div>
              <div style={s.locAddr}>{loc.address}</div>
            </div>
          </div>
          {/* Market */}
          <div style={s.cellC}><TierPill market={loc.market} cls={loc.mktClass} /></div>
          {/* License */}
          <div style={{ ...s.cellC, fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-2)' }}>{loc.license}</div>
          {/* Spy status */}
          <div style={s.cellC}>
            {loc.active ? (
              <div style={s.healthWrap}>
                <div style={s.dot(healthColor(loc.health))} />
                <div style={{ textAlign: 'left' }}>
                  <div style={s.monoSm(healthColor(loc.health))}>{loc.healthLabel}</div>
                  <div style={s.monoXs('var(--text-3)')}>{loc.lastRun}</div>
                </div>
              </div>
            ) : (
              <div style={s.monoXs('var(--text-3)')}>Paused</div>
            )}
          </div>
          {/* Rival relationships */}
          <div style={{ ...s.cell, ...s.slots }}>
            <span style={{ color: 'var(--accent)' }}>{loc.track} track</span>
          </div>
          {/* Status */}
          <div style={s.cellC}>
            <Pill variant="chip" style={{ ...s.statusPill(loc.active), fontWeight: 700, letterSpacing: '0.06em' }}>
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: loc.active ? 'var(--accent)' : 'var(--text-3)', display: 'inline-block' }} />
              {loc.active ? 'Active' : 'Inactive'}
            </Pill>
          </div>
          {/* Actions */}
          <div style={s.actions} onClick={e => e.stopPropagation()}>
            <button style={s.rowBtn} onClick={() => navigate(`/locations/${loc.id}`)}>{Ic.dash}Dashboard</button>
            {loc.active ? (
              <button style={s.rowBtnDanger} onClick={() => setShowDeactivateModal(loc.id)}>Deactivate</button>
            ) : (
              <button style={s.rowBtn} onClick={() => reactivate(loc.id)}>Reactivate</button>
            )}
          </div>
        </div>

        {/* Expanded detail */}
        {isExpanded && (
          <div style={s.detail}>
            <div style={s.detailGrid}>
              {/* Recent spy runs */}
              <div>
                <SectionLabel style={{ marginBottom: 8 }}>Recent spy runs</SectionLabel>
                {loc.log.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {loc.log.map((r, i) => (
                      <div key={i} style={s.spyLogRow}>
                        <div style={{ width: 6, height: 6, borderRadius: '50%', background: logDotColor(r.s), flexShrink: 0 }} />
                        <div style={{ ...s.monoXs('var(--text-3)'), width: 62, flexShrink: 0 }}>{r.t}</div>
                        <div style={s.monoXs('var(--text-2)')}>{r.n}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={s.emptyMini}>No spy runs recorded yet. First run is scheduled for the next 2–5 AM Pacific window.</div>
                )}
                <div style={{ ...s.dsRow(false), marginTop: 8 }}>
                  <span style={s.dsKey}>Items collected</span>
                  <span style={s.dsVal('var(--accent)')}>{loc.items > 0 ? loc.items.toLocaleString() : '—'}</span>
                </div>
                <div style={s.dsRow(true)}>
                  <span style={s.dsKey}>Spy pipeline</span>
                  <span style={s.dsVal('var(--text-1)')}>CannaSpy cloud</span>
                </div>
              </div>
              {/* Competitors */}
              <div>
                <SectionLabel style={{ marginBottom: 8 }}>Competitors ({loc.competitors.length})</SectionLabel>
                <DataTable<CompRow>
                  columns={compColumns}
                  rows={loc.competitors.map((c) => ({ name: c, blocked: loc.blocked.includes(c) }))}
                  rowKey={(r) => r.name}
                  empty="No rivals tracked at this location yet. Add competitors to start monitoring."
                />
              </div>
              {/* Location details */}
              <div>
                <SectionLabel style={{ marginBottom: 8 }}>Location details</SectionLabel>
                {([
                  ['Market category', loc.market, 'var(--text-1)'],
                  ['Location plan', 'Manage in Billing', 'var(--accent)'],
                  ['DCC license', loc.license, 'var(--text-1)'],
                  ['Active since', '—', 'var(--text-1)'],
                ] as [string, string, string][]).map(([k, v, c], i, arr) => (
                  <div key={k} style={s.dsRow(i === arr.length - 1)}>
                    <span style={s.dsKey}>{k}</span>
                    <span style={s.dsVal(c)}>{v}</span>
                  </div>
                ))}
              </div>
            </div>
            {/* Detail actions */}
            <div style={s.detailActions}>
              <button style={s.rowBtn} onClick={() => navigate(`/locations/${loc.id}`)}>{Ic.dash}Open dashboard</button>
              <button style={s.rowBtn} onClick={() => showToast('Competitor discovery opened')}>{Ic.search}Add competitors</button>
              <button
                style={s.rowBtn}
                disabled={refreshing.has(loc.id)}
                onClick={() => requestRefresh(loc.id)}
                data-testid={`update-menu-${loc.id}`}
              >{Ic.spy}{refreshing.has(loc.id) ? 'Queuing…' : 'Update now'}</button>
              {loc.unavailableCompetitorId && (
                <button
                  style={s.rowBtn}
                  onClick={() => navigate(`/competitors/${loc.unavailableCompetitorId}`)}
                  data-testid={`request-menu-review-${loc.id}`}
                >{Ic.search}Request menu review</button>
              )}
              <button style={s.rowBtn} onClick={() => showToast('Edit location opened')}>{Ic.edit}Edit</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  function Section({ title, sub, rows, cols, testid }: { title: string; sub: string; rows: Location[]; cols: [string, 'left' | 'center' | 'right'][]; testid: string }) {
    return (
      <Card style={s.section} data-testid={testid}>
        <div style={s.secHead}>
          <span style={s.secTitle}>{title}</span>
          <span style={s.secSub}>{sub}</span>
        </div>
        <div style={s.headerRow}>
          {cols.map(([label, align], i) => <div key={i} style={s.th(align)}>{label}</div>)}
        </div>
        {rows.length > 0
          ? rows.map(loc => <LocRow key={loc.id} loc={loc} />)
          : <div style={{ padding: '28px 20px', textAlign: 'center', fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-3)' }}>None in this group.</div>}
      </Card>
    );
  }

  const activeCols: [string, 'left' | 'center' | 'right'][] = [
    ['Location', 'left'], ['Market', 'center'], ['License', 'center'], ['Spy status', 'center'], ['Rivals', 'center'], ['Status', 'center'], ['Actions', 'right'],
  ];
  const inactiveCols: [string, 'left' | 'center' | 'right'][] = [
    ['Location', 'left'], ['Market', 'center'], ['License', 'center'], ['Last spy run', 'center'], ['Rivals', 'center'], ['Status', 'center'], ['Actions', 'right'],
  ];

  return (
    <div style={s.root}>
      {/* Topbar */}
      <div style={s.topbar}>
        <div style={s.tbInner}>
          <button style={s.back} onClick={() => navigate(-1)}>{Ic.back}Back</button>
          <div style={s.tbDivider} />
          <div>
            <div style={s.tbTitle}>Location Management</div>
            <div style={s.tbSub}>{locsLoading ? 'Loading…' : `${locs.length} location${locs.length !== 1 ? 's' : ''}`}</div>
          </div>
          <div style={s.tbActions}>
            <button className="btn" style={{ fontSize: 11, padding: '7px 13px' }} onClick={requestAllRefreshes} disabled={refreshing.size > 0}>
              {Ic.refresh}{refreshing.size > 0 ? 'Queuing…' : 'Update all now'}
            </button>
            <button className="btn btn-primary" style={{ fontSize: 11, padding: '7px 13px' }} onClick={() => setShowAddModal(true)}>
              {Ic.plus}Add location
            </button>
          </div>
        </div>
      </div>

      {/* Stats bar */}
      <div style={s.statsBar}>
        <StatTile size="sm" tone="accent" value={locs.length} label="Total locations" style={{ minWidth: 108 }} />
        <StatTile size="sm" value={active.length} label="Active" style={{ minWidth: 88 }} />
        <StatTile size="sm" tone="blocking" value={inactive.length} label="Inactive" style={{ minWidth: 92 }} />
        <div style={s.statsSep} />
        <StatTile size="sm" tone="accent" value={totalTrack} label="Tracked rivals" style={{ minWidth: 108 }} />
        <div style={s.statsSep} />
        <StatTile size="sm" value={totalSkus.toLocaleString()} label="SKUs tracked" style={{ minWidth: 108 }} />
        <div style={s.spyStatus}>
          <div style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--accent)', flexShrink: 0 }} />
          Central updates · Daily and on demand
        </div>
      </div>

      {/* Content */}
      <div style={s.content}>
        {locsError ? (
          <div style={s.centerBlock}>
            <div style={{ maxWidth: 420, textAlign: 'center' }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-1)', marginBottom: 8 }}>Couldn’t load your locations</div>
              <div style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.6, marginBottom: 16 }}>We couldn’t reach the locations service. Your locations are safe — retry to load them here.</div>
              <button className="btn btn-primary" style={{ margin: '0 auto' }} onClick={loadLocs}>{Ic.refresh}Retry</button>
            </div>
          </div>
        ) : locsLoading ? (
          <div style={{ ...s.centerBlock, fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--text-3)' }}>Pulling your locations…</div>
        ) : locs.length === 0 ? (
          <div style={s.centerBlock}>
            <div style={{ maxWidth: 420, textAlign: 'center' }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-1)', marginBottom: 8 }}>No locations yet</div>
              <div style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.6, marginBottom: 16 }}>Add your first location to start monitoring rivals. Spying begins within the next scheduled window (2–5 AM Pacific).</div>
              <button className="btn btn-primary" style={{ margin: '0 auto' }} onClick={() => setShowAddModal(true)}>{Ic.plus}Add location</button>
            </div>
          </div>
        ) : (
          <>
            <Section testid="locations-active" title="Active locations" sub={`— ${active.length} of ${locs.length} · Click a row to expand details`} rows={active} cols={activeCols} />
            <Section testid="locations-inactive" title="Inactive locations" sub={`— ${inactive.length} · Updates paused`} rows={inactive} cols={inactiveCols} />
          </>
        )}
      </div>

      {/* Add modal */}
      {showAddModal && (
        <div style={s.modalOverlay} onClick={() => setShowAddModal(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()} data-testid="location-add-modal">
            <div style={s.modalTitle}>Add a new location</div>
            <div style={s.modalSub}>Choose this location’s rival allowance in Billing after adding it. Your 14-day trial starts only when usable menu data is ready.</div>
            {([['Location name', 'e.g. WeHo Flagship'], ['Street address', 'e.g. 8001 Santa Monica Blvd, West Hollywood, CA']] as [string, string][]).map(([label, placeholder]) => (
              <div key={label} style={s.formRow}>
                <label style={s.formLabel}>{label}</label>
                <input type="text" placeholder={placeholder} style={s.formInput} />
              </div>
            ))}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
              <div>
                <label style={s.formLabel}>Market category</label>
                <select style={{ ...s.formInput, appearance: 'none', WebkitAppearance: 'none' }}>
                  <option>Standard</option><option>Competitive</option><option>Hot</option><option>Elite</option>
                </select>
              </div>
              <div>
                <label style={s.formLabel}>DCC license #</label>
                <input type="text" placeholder="C10-0000000-LIC" style={s.formInput} />
              </div>
            </div>
            <div style={s.modalFooter}>
              <button className="btn" style={{ fontSize: 11 }} onClick={() => setShowAddModal(false)}>Cancel</button>
              <button className="btn btn-primary" style={{ fontSize: 11 }} onClick={() => { setShowAddModal(false); showToast('Location added · Spy scheduled for 2 AM'); }}>Add location</button>
            </div>
          </div>
        </div>
      )}

      {/* Deactivate modal */}
      {showDeactivateModal && (() => {
        const loc = locs.find(l => l.id === showDeactivateModal);
        return loc ? (
          <div style={s.modalOverlay} onClick={() => setShowDeactivateModal(null)}>
            <div style={s.modal} onClick={e => e.stopPropagation()} data-testid="location-deactivate-modal">
              <div style={s.modalTitle}>Deactivate {loc.name}?</div>
              <div style={s.modalSub}>Spying on this location will pause immediately. Billing stops for this location. You can reactivate at any time.</div>
              <div style={s.modalFooter}>
                <button className="btn" style={{ fontSize: 11 }} onClick={() => setShowDeactivateModal(null)}>Cancel</button>
                <button className="btn btn-cancel" style={{ fontSize: 11 }} onClick={() => deactivate(loc.id)}>Deactivate</button>
              </div>
            </div>
          </div>
        ) : null;
      })()}

      {toast && <div style={s.toast}>{toast}</div>}
    </div>
  );
}
